import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import './RodentStations.css';

const RodentStations = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const fetchAlerts = async () => {
    try {
      const response = await fetch(`${config.TRAP_API_BASE_URL}/api/alerts`);
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Error fetching trap alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 2000);
    return () => clearInterval(interval);
  }, []);

  const getAlertForDevice = (deviceId) => {
    return alerts.find(a => a.device_id === deviceId);
  };

  const handleAcknowledge = async (alertId) => {
    try {
      // Optimistic state update
      setAlerts(prev => prev.filter(a => a.id !== alertId && a.device_id !== alertId));
      
      const response = await fetch(`${config.TRAP_API_BASE_URL}/api/acknowledge/${alertId}`, {
        method: 'POST',
      });
      if (response.ok) {
        fetchAlerts();
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      fetchAlerts();
    }
  };


  return (
    <div className="rodent-safe-area">
      <div className="rodent-container">
        <div className="rodent-content">
          {/* Header */}
          <div className="rodent-header-row">
            <button className="back-button" onClick={() => navigate('/')}>
              <span className="back-icon">‹</span>
            </button>
            <div className="header-title-group">
              <div className="header-title-main">RODENT STATIONS</div>
              <div className="header-title-sub">VERSA NETWORK</div>
            </div>
            <button
              onClick={() => navigate('/trap-alert-history')}
              style={{ marginLeft: 'auto', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#fff', padding: '8px 12px', color: '#334155', cursor: 'pointer' }}
            >
              Alert history
            </button>
          </div>

          {loading ? (
            <div className="loading-state">Connecting to Versa Network...</div>
          ) : (
            <div className="devices-container">
              {alerts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  No active rodent alerts at this time.
                </div>
              ) : (
                alerts.map(alert => (
                  <div key={alert.id ?? `${alert.device_id}-${alert.triggered_at}`} className="card active-alert-card">
                    <div className="card-content">
                      <div className="left-section">
                        <div className="device-name">{alert.device_id}</div>
                        <div className="status-container">
                          <div
                            className="status-dot pulsing-red-dot"
                            style={{ backgroundColor: '#f43f5e' }}
                          />
                          <div className="status-text" style={{ color: '#f43f5e', fontWeight: '600' }}>
                            🚨 ALERT ACTIVE
                          </div>
                        </div>
                        <div style={{ marginTop: '6px', fontSize: '13px', fontWeight: 600, color: '#b91c1c' }}>
                          {alert.event_type || 'Rodent detection'}
                        </div>
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                          {new Date(alert.triggered_at).toLocaleString()}
                        </div>
                        <div className="device-location-label" style={{ marginTop: '8px', fontSize: '13px', color: '#64748b' }}>
                          📍 {alert.ship_name} • {alert.location_section}
                        </div>
                      </div>
                      
                      <div className="right-section">
                        <div className="device-image">
                          <img
                            src="/assets/RodentStation.png"
                            alt="Rodent Station"
                            className="hardware-icon"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <button
                      className="view-button"
                      onClick={() => setSelectedDevice({
                        id: alert.device_id,
                        name: alert.device_id,
                        ship: alert.ship_name,
                        dept: alert.department,
                        loc: alert.location_section,
                        area: alert.area_cabin,
                        text: alert.location_text
                      })}
                    >
                      <span className="view-button-text">View</span>
                      <span className="view-button-arrow">›</span>
                    </button>
                    <button
                      className="ack-btn"
                      onClick={() => handleAcknowledge(alert.id ?? alert.device_id)}
                    >
                      Acknowledge
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mockup Phone Details Overlay */}
      {selectedDevice && (() => {
        const activeAlert = getAlertForDevice(selectedDevice.id);
        const isDeviceInAlert = !!activeAlert;

        return (
          <div className="modal-overlay" onClick={() => setSelectedDevice(null)}>
            <div className="phone-wrapper" onClick={(e) => e.stopPropagation()}>
              
              {/* Phone notch */}
              <div className="phone-notch">
                <div className="camera-dot"></div>
              </div>

              {/* Close Button */}
              <button className="phone-close-btn" onClick={() => setSelectedDevice(null)}>✕</button>

              {/* Phone Header */}
              <header className="phone-header">
                <h1 className="phone-title">GLOBAL ALERTZ</h1>
                <p className="phone-subtitle">TRAP MONITORING SYSTEM</p>
              </header>

              {/* Phone Content Screen */}
              <main className="phone-content">
                {isDeviceInAlert ? (
                  <div className="alert-container">
                    <div className="alert-header">
                      <div className="alert-label">ALERT</div>
                      <div className="alert-type-badge rodent" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '-1px' }}>
                          <path d="M12 22a8 8 0 0 0 8-8c0-5.5-4.5-10-8-10S4 8.5 4 14a8 8 0 0 0 8 8z"/>
                          <path d="M16 14a4 4 0 0 0-8 0"/>
                          <path d="M12 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
                        </svg>
                        <span>Rodent Station</span>
                      </div>
                    </div>

                    <div className="details-stack">
                      <div className="detail-box">
                        <span className="detail-label">Event Type</span>
                        <span className="detail-val">{activeAlert.event_type || 'Rodent detection'}</span>
                      </div>

                      <div className="detail-box">
                        <span className="detail-label">Alert Date &amp; Time</span>
                        <span className="detail-val">{new Date(activeAlert.triggered_at).toLocaleString()}</span>
                      </div>

                      <div className="detail-box">
                        <span className="detail-label">Ship Name</span>
                        <span className="detail-val">{selectedDevice.ship}</span>
                      </div>

                      <div className="detail-box">
                        <span className="detail-label">Department</span>
                        <span className="detail-val">{selectedDevice.dept}</span>
                      </div>

                      <div className="detail-box">
                        <span className="detail-label">General Location</span>
                        <span className="detail-val">{selectedDevice.loc}</span>
                      </div>

                      <div className="detail-box">
                        <span className="detail-label">Area</span>
                        <span className="detail-val">{selectedDevice.area}</span>
                      </div>

                      <div className="detail-box">
                        <span className="detail-label">Location Description</span>
                        <p className="detail-val desc">{selectedDevice.text}</p>
                      </div>
                    </div>

                    <button 
                      className="ack-btn" 
                      onClick={() => {
                        handleAcknowledge(activeAlert.id ?? selectedDevice.id);
                        setSelectedDevice(null);
                      }}
                    >
                      Acknowledge
                    </button>
                  </div>
                ) : (
                  <div className="idle-container">
                    <div className="radar-circle">
                      <div className="radar-pulse"></div>
                      <div className="radar-dot"></div>
                    </div>
                    <h2 className="idle-title" style={{ color: '#fff', fontSize: '1.2rem' }}>{selectedDevice.name} Normal</h2>
                    <p className="idle-text" style={{ marginBottom: '24px' }}>Station is online. No events detected.</p>
                    
                  </div>
                )}
              </main>

            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RodentStations;
